import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface PortalPageHeaderProps {
  /** 32px glyph rendered before the title. */
  icon?: ReactNode;
  title: ReactNode;
  /** Pills or badges rendered inline after the title (e.g. a status pill). */
  titleAdornment?: ReactNode;
  description?: ReactNode;
  breadcrumb?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

/**
 * Page title row: 36px medium title (+ optional 32px icon / adornment) on the
 * left, actions on the right. Uses a `role="heading"` div because the
 * unlayered normalize in index.css overrides Tailwind font-size on `h1`.
 */
export function PortalPageHeader({
  icon,
  title,
  titleAdornment,
  description,
  breadcrumb,
  actions,
  className,
}: PortalPageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-3', className)}>
      {breadcrumb}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
            {icon ? (
              <span
                aria-hidden="true"
                className="flex size-8 shrink-0 items-center justify-center text-white [&>svg]:size-8"
              >
                {icon}
              </span>
            ) : null}
            <div
              role="heading"
              aria-level={1}
              className="min-w-0 truncate text-4xl font-medium leading-10 text-white"
            >
              {title}
            </div>
            {titleAdornment}
          </div>
          {description ? (
            <div className="text-sm text-seeker-400">{description}</div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-4">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}
